import DateRangeFilter, { DateRangeFilterChange } from "@/components/filters/DateRangeFilter";
import { Agent, useAppData } from "@/contexts/AppDataContext";
import { calculateDateRange, DATE_RANGE_KEYS } from "@/utils/dateRangeUtils";
import React, { useEffect } from "react";
import { AgentFilter } from "../filters/AgentFilter";
import { CallQueueFilter } from "../filters/CallQueueFilter";
import { DirectAgentFilter } from "../filters/DirectAgentFilter";
import { QueueFilter } from "../filters/QueueFilter";
import { CallQueueType } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { FilterBarProps} from "@/types";

const FilterBar: React.FC<FilterBarProps> = ({
  onFilterChange,
  initialDateRangeKey = DATE_RANGE_KEYS.LAST_30_DAYS,
  initialSelectedQueues = [],
  initialSelectedAgents = [],
  initialSelectedQueueType = CallQueueType.CALL_QUEUE,
  initialDateRange,
  quickRanges,
  className,
  sectionType,
}) => {
  const { queuesResponse, uniqueAgents, directAgentGroups, loaded: appDataLoading } = useAppData();
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(initialDateRangeKey);
  const [selectedQueue, setSelectedQueue] = React.useState<string[]>(initialSelectedQueues);
  const [selectedAgents, setSelectedAgents] = React.useState<string[]>(initialSelectedAgents);
  const [selectedQueueType, setSelectedQueueType] = React.useState<CallQueueType | null>(initialSelectedQueueType);
  const [dateRange, setDateRange] = React.useState<{
    startDate: Date;
    endDate: Date;
    previousStartDate: Date;
    previousEndDate: Date;
  }>(() => {
    const range = calculateDateRange(initialDateRangeKey);
    return {
      startDate: range.from,
      endDate: range.to,
      previousStartDate: range.previousFrom,
      previousEndDate: range.previousTo,
    };
  });
  const [firstRender, setFirstRender] = React.useState(true);

  // Get available agents based on selected queues
  const availableAgents = React.useMemo(() => {
    if (selectedQueue.length === 0) {
      // If no queue is selected, return all agents
      return uniqueAgents.filter(agent => agent.queueType === selectedQueueType);
    }
    // Return agents that belong to selected queues
    const queueAgents = queuesResponse
      .filter(queue => selectedQueue.includes(queue.type === CallQueueType.CALL_QUEUE ? queue.microsoftId : queue.id?.toString()))
      .flatMap(queue => queue.agents || []);

    return Array.from(
      new Map(queueAgents.map((agent) => [agent.microsoftId, agent])).values()
    );
  }, [queuesResponse, selectedQueue, appDataLoading, selectedQueueType]);

  // When queuesResponse changes, reset the selected queue and agents
  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    setSelectedQueue([]);
    setSelectedAgents([]);

    // If the selected queue type is direct agent group and there are no direct agent groups, set the selected queue type to agent
    let newSelectedQueueType = selectedQueueType;
    if(sectionType == SectionType.INSTANT_CONNECT_INSIGHT &&
      selectedQueueType == CallQueueType.DIRECT_AGENT_GROUP &&
      (directAgentGroups || []).length === 0
    ) {
      newSelectedQueueType = CallQueueType.AGENT;
      setSelectedQueueType(newSelectedQueueType);
    }

    onFilterChange({
      dateRange: {
        key: dateRangeKey,
        ...dateRange
      },
      queues: [],
      agents: [],
      selectedQueueType: newSelectedQueueType
    });

  }, [queuesResponse]);

  // Trigger initial date range calculation
  useEffect(() => {
      const range = initialDateRange?.key === DATE_RANGE_KEYS.CUSTOM
      ? {
        from: new Date(initialDateRange?.startDate),
        to: new Date(initialDateRange?.endDate),
        previousFrom: new Date(initialDateRange?.previousStartDate),
        previousTo: new Date(initialDateRange?.previousEndDate),
      }
      : calculateDateRange(initialDateRange?.key);

      setDateRange({
        startDate: range?.from,
        endDate: range?.to,
        previousStartDate: range?.previousFrom,
        previousEndDate: range?.previousTo,
      });
  }, [initialDateRange]);

  const handleDateRangeChange = (range: DateRangeFilterChange) => {
    setDateRangeKey(range.key);
    setDateRange({
      startDate: range.startDate,
      endDate: range.endDate,
      previousStartDate: range.previousStartDate,
      previousEndDate: range.previousEndDate,
    });
    onFilterChange({
      dateRange: {
        key: range.key,
        startDate: range.startDate,
        endDate: range.endDate,
        previousStartDate: range.previousStartDate,
        previousEndDate: range.previousEndDate,
      },
      queues: selectedQueue,
      agents: selectedAgents,
      selectedQueueType
    });
  };

  const handleQueueChange = (queues: string[], callQueueType?: CallQueueType) => {
    setSelectedQueue(queues);
    const queueType = callQueueType || selectedQueueType;
    setSelectedQueueType(queueType);
    // Reset selected agents when queues change
    setSelectedAgents([]);
    onFilterChange({
      dateRange: {
        key: dateRangeKey,
        ...dateRange
      },
      queues,
      agents: [],
      selectedQueueType: queueType
    });
  };

  const handleAgentChange = (agents: string[]) => {
    setSelectedAgents(agents);
    onFilterChange({
      dateRange: {
        key: dateRangeKey,
        ...dateRange
      },
      queues: selectedQueue,
      agents,
      selectedQueueType
    });
  };

  const handleDirectAgentChange = (values: string[], filterType: CallQueueType) => {
    setSelectedQueueType(filterType);

    if (filterType === CallQueueType.DIRECT_AGENT_GROUP) {
      // Update queues for direct agent groups
      setSelectedQueue(values);
      setSelectedAgents([]); // Clear agents when selecting groups
      onFilterChange({
        dateRange: {
          key: dateRangeKey,
          ...dateRange
        },
        queues: values,
        agents: [],
        selectedQueueType: filterType
      });
    } else if (filterType === CallQueueType.AGENT) {
      // Update agents for individual agents
      setSelectedAgents(values);
      setSelectedQueue([]); // Clear queues when selecting agents
      onFilterChange({
        dateRange: {
          key: dateRangeKey,
          ...dateRange
        },
        queues: [],
        agents: values,
        selectedQueueType: filterType
      });
    }
  };

  const renderFilters = () => {
    switch (sectionType) {
      case SectionType.CALL_OVERVIEW:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            <QueueFilter
              value={selectedQueue}
              onChange={(queues) => handleQueueChange(queues, CallQueueType.CALL_QUEUE)}
              className={className}
            />
          </>
        );

      case SectionType.INSTANT_CONNECT_INSIGHT:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            <DirectAgentFilter
              value={selectedQueueType === CallQueueType.AGENT ? selectedAgents : selectedQueue}
              filterType={selectedQueueType !== CallQueueType.CALL_QUEUE ? selectedQueueType : CallQueueType.DIRECT_AGENT_GROUP}
              onChange={handleDirectAgentChange}
              className={className}
            />
          </>
        );

      case SectionType.TRENDS:
        return (
          <DateRangeFilter
            value={dateRangeKey}
            onChange={handleDateRangeChange}
            quickRanges={quickRanges}
            initialDateRange={initialDateRange}
            className={className}
          />
        );

      default:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            {/* <CallQueueFilter
              value={selectedQueue}
              onChange={(queues) => handleQueueChange(queues)}
              queueType={selectedQueueType}
              className={className}
            /> */}
            <AgentFilter
              value={selectedAgents}
              onChange={handleAgentChange}
              availableAgents={availableAgents}
              className={className}
            />
          </>
        );
    }
  };

  return (
    <div className="mb-4 flex flex-col md:flex-row align-center gap-2">
      {renderFilters()}
    </div>
  );
};

export default FilterBar;
